import React, { useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import Menu from '@mui/material/Menu'
import MenuItem from '@mui/material/MenuItem'
import EditIcon from '@mui/icons-material/EditOutlined'
import { RAG } from 'features/dashboard/charts/scoreColour'

const SEVERITIES = [
  { value: 'critical', label: 'Critical' },
  { value: 'high', label: 'High' },
  { value: 'medium', label: 'Medium' },
  { value: 'low', label: 'Low' },
].map(s => ({ ...s, colour: RAG[s.value].fg, bg: RAG[s.value].bg }))

const SEVERITY_BY_VALUE = SEVERITIES.reduce((m, s) => ({ ...m, [s.value]: s }), {})

const SeverityQuickEdit = ({ value, onChange, disabled = false, size = 'md' }) => {
  const [anchorEl, setAnchorEl] = useState(null)
  const [optimisticValue, setOptimisticValue] = useState(null)
  const lockRef = useRef(false)

  useEffect(() => {
    setOptimisticValue(null)
  }, [value])

  const current = optimisticValue ?? value
  const sev = SEVERITY_BY_VALUE[current] || SEVERITY_BY_VALUE.low

  const open = (e) => {
    if (disabled || lockRef.current) return
    e.stopPropagation()
    setAnchorEl(e.currentTarget)
  }

  const close = (e) => {
    if (e) e.stopPropagation()
    setAnchorEl(null)
  }

  const select = async (e, nextValue) => {
    e.stopPropagation()
    setAnchorEl(null)
    if (nextValue === current) return
    if (!onChange) return

    setOptimisticValue(nextValue)
    lockRef.current = true
    try {
      await onChange(nextValue)
    } catch (err) {
      setOptimisticValue(null)
    } finally {
      lockRef.current = false
    }
  }

  return (
    <>
      <Target
        type="button"
        $disabled={disabled}
        onClick={open}
        title={disabled ? sev.label : 'Click to change severity'}
        aria-haspopup={disabled ? undefined : 'menu'}
        aria-label={disabled ? `Severity: ${sev.label}` : `Severity: ${sev.label}. Click to change`}
      >
        <Pip $colour={sev.colour} $bg={sev.bg} $disabled={disabled} $small={size === 'sm'}>
          {sev.label}
          {!disabled && <EditIcon style={{ fontSize: 14 }} aria-hidden="true" />}
        </Pip>
      </Target>

      <Menu
        anchorEl={anchorEl}
        open={!!anchorEl}
        onClose={close}
        onClick={(e) => e.stopPropagation()}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { minWidth: 180, mt: 0.5 } }}
      >
        {SEVERITIES.map((s) => (
          <MenuItem
            key={s.value}
            selected={s.value === current}
            onClick={(e) => select(e, s.value)}
            sx={{ minHeight: 44, fontSize: 15 }}
          >
            <Swatch style={{ background: s.bg, color: s.colour, borderColor: s.colour }} />
            {s.label}
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}

export default SeverityQuickEdit

const Target = styled.button`
  display: inline-flex;
  align-items: center;
  min-height: 40px;
  padding: 0;
  border: none;
  background: none;
  font: inherit;
  cursor: ${({ $disabled }) => ($disabled ? 'default' : 'pointer')};
  flex-shrink: 0;

  &:focus-visible {
    outline: 2px solid #0C1229;
    outline-offset: 2px;
    border-radius: 999px;
  }
`

const Pip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: ${({ $small }) => ($small ? '2px 8px' : '4px 10px')};
  font-size: 14px;
  font-weight: 700;
  line-height: 1.3;
  border-radius: 999px;
  background: ${({ $bg }) => $bg};
  color: ${({ $colour }) => $colour};
  border: 1px solid transparent;
  transition: border-color 0.12s;

  ${Target}:hover & {
    border-color: ${({ $colour, $disabled }) => ($disabled ? 'transparent' : $colour)};
  }
`

const Swatch = styled.span`
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 8px;
  border: 1px solid;
`
